// Infrastructure damage forecasting via Monte Carlo ensemble simulation.
//
// Clones the live fire engine and runs it forward N times against the real
// weather timeline. Produces:
//   * burnProb[r][c]     — probability a cell burns within the horizon
//   * lossCurve          — expected infrastructure cells lost vs. time
//   * expectedValueLoss  — expected weighted value lost (critical assets count more)
//   * timeToImpact[r][c] — mean hours until first fire arrival (infra cells only)
//
// Runs are chunked through the event loop so the UI stays responsive.

import {
  STATE_BURNING,
  STATE_SMOLDERING,
  STATE_EMPTY,
  STATE_INFRASTRUCTURE_BURNT,
  STATE_WATER,
  STATE_INFRASTRUCTURE,
} from "./constants";

// Small deterministic PRNG so each ensemble member differs but results are
// reproducible for a given engine state.
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const yieldToUI = () => new Promise((res) => setTimeout(res, 0));

export const runDamageForecast = async ({
  engine,
  weatherTimeline,
  startMs,
  horizonHours = 48,
  runs = 12,
  stepHours = 1,
  onProgress,
}) => {
  const { rows, cols } = engine;
  const burnCount = Array(rows)
    .fill(null)
    .map(() => new Float32Array(cols));
  const impactHoursSum = Array(rows)
    .fill(null)
    .map(() => new Float32Array(cols));
  const impactRuns = Array(rows)
    .fill(null)
    .map(() => new Uint16Array(cols));
  const steps = Math.ceil(horizonHours / stepHours);
  const lossByStep = new Float64Array(steps + 1);
  let valueLossSum = 0;

  const baseStats = engine.stats();

  // Cells already burned/burning in the live state don't count as new losses
  const alreadyBurned = Array(rows)
    .fill(null)
    .map((_, r) => {
      const row = new Uint8Array(cols);
      for (let c = 0; c < cols; c++) {
        const s = engine.state[r][c];
        if (
          s === STATE_BURNING ||
          s === STATE_SMOLDERING ||
          s === STATE_INFRASTRUCTURE_BURNT ||
          (s === STATE_EMPTY && engine.initialGridType[r][c] !== STATE_WATER)
        )
          row[c] = 1;
      }
      return row;
    });

  for (let run = 0; run < runs; run++) {
    const rng = mulberry32(0x9e3779b9 ^ (run * 2654435761));
    const sim = engine.clone(rng);
    const burnedAt = Array(rows)
      .fill(null)
      .map(() => new Float32Array(cols).fill(NaN));

    for (let step = 0; step < steps; step++) {
      const t = startMs + step * stepHours * 3600000;
      sim.step(weatherTimeline.at(t), stepHours);

      // Track first fire arrival per cell
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const s = sim.state[r][c];
          if (
            (s === STATE_BURNING ||
              s === STATE_SMOLDERING ||
              s === STATE_EMPTY ||
              s === STATE_INFRASTRUCTURE_BURNT) &&
            Number.isNaN(burnedAt[r][c]) &&
            sim.initialGridType[r][c] !== STATE_WATER &&
            !alreadyBurned[r][c]
          ) {
            burnedAt[r][c] = (step + 1) * stepHours;
          }
        }
      }

      const st = sim.stats();
      lossByStep[step + 1] +=
        st.burntInfrastructure - baseStats.burntInfrastructure;
    }

    const endStats = sim.stats();
    valueLossSum += endStats.valueLost - baseStats.valueLost;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!Number.isNaN(burnedAt[r][c])) {
          burnCount[r][c] += 1;
          if (engine.initialGridType[r][c] === STATE_INFRASTRUCTURE) {
            impactHoursSum[r][c] += burnedAt[r][c];
            impactRuns[r][c] += 1;
          }
        }
      }
    }

    onProgress?.((run + 1) / runs);
    await yieldToUI();
  }

  const burnProb = Array(rows)
    .fill(null)
    .map((_, r) => {
      const row = new Float32Array(cols);
      for (let c = 0; c < cols; c++) row[c] = burnCount[r][c] / runs;
      return row;
    });
  const timeToImpact = Array(rows)
    .fill(null)
    .map((_, r) => {
      const row = new Float32Array(cols).fill(NaN);
      for (let c = 0; c < cols; c++) {
        if (impactRuns[r][c] > 0)
          row[c] = impactHoursSum[r][c] / impactRuns[r][c];
      }
      return row;
    });

  const lossCurve = [];
  for (let step = 0; step <= steps; step++) {
    lossCurve.push({
      hour: step * stepHours,
      expectedLoss: lossByStep[step] / runs,
    });
  }

  return {
    burnProb,
    timeToImpact,
    lossCurve,
    expectedInfraLoss: lossByStep[steps] / runs,
    expectedValueLoss: valueLossSum / runs,
    horizonHours,
    runs,
    generatedAtMs: startMs,
  };
};
